import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { apiService } from "../../services/api";
import { Shop, RouteEntity } from "../../types";
import MallMap from "../../components/MallMap/MallMap";
import ErrorPage from "../ErrorPage/ErrorPage";
import styles from "./Mobility.module.css";

export default function Mobility() {
  const [searchParams] = useSearchParams();
  const extractRouteMessage = (
    message: RouteEntity["message"]
  ): string | undefined => {
    if (!message) return undefined;
    if (typeof message === "string") return message;
    if (typeof message === "object" && "text" in message) {
      return (message as any).text as string;
    }
    return undefined;
  };
  const {
    state,
    setCurrentFloor,
    setSelectedShop,
    setActiveRoutePoints,
    setActiveRouteMessage,
    setScreensaverActive,
  } = useApp();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasBuiltRouteRef = useRef<string>("");
  const isBuildingRef = useRef(false);

  // Получаем параметры URL напрямую - они стабильны
  const terminalName = searchParams.get("terminalName");
  const shopId = searchParams.get("shopId");

  // Мемоизируем ключ маршрута для проверки изменений
  const routeKey = useMemo(() => {
    if (!terminalName || !shopId) return null;
    return `${terminalName}-${shopId}`;
  }, [terminalName, shopId]);

  // Отключаем screensaver на странице mobility (только один раз)
  useEffect(() => {
    setScreensaverActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Предотвращаем параллельные вызовы
    if (isBuildingRef.current) {
      return;
    }

    // Проверяем, что у нас есть необходимые параметры
    if (!routeKey) {
      if (hasBuiltRouteRef.current !== "empty") {
        setError("Отсутствуют обязательные параметры: terminalName и shopId");
        setLoading(false);
        hasBuiltRouteRef.current = "empty";
      }
      return;
    }

    // Проверяем, не строили ли мы уже этот маршрут
    if (hasBuiltRouteRef.current === routeKey) {
      return;
    }

    isBuildingRef.current = true;

    const buildRoute = async () => {
      try {
        setLoading(true);
        setError(null);

        // Находим стойку по имени
        const terminals = await apiService.getTerminals({ active: true });
        const terminal = terminals.find((t) => t.name === terminalName);

        if (!terminal) {
          setActiveRoutePoints(undefined);
          setActiveRouteMessage(undefined);
          setError(`Стойка с именем "${terminalName}" не найдена`);
          setLoading(false);
          hasBuiltRouteRef.current = routeKey || "error";
          isBuildingRef.current = false;
          return;
        }

        // Находим объект по ID
        let shop: Shop;
        try {
          if (!shopId) {
            throw new Error("Shop ID is required");
          }
          shop = await apiService.getShopById(shopId);
        } catch (e) {
          setError(`Объект с ID "${shopId}" не найден`);
          setLoading(false);
          hasBuiltRouteRef.current = routeKey || "error";
          isBuildingRef.current = false;
          return;
        }

        // Устанавливаем этаж стойки (маршрут начинается от стойки)
        // Только если этаж действительно изменился
        if (terminal.floor && state.currentFloor !== terminal.floor) {
          setCurrentFloor(terminal.floor);
        }

        // Находим маршрут
        const routes: RouteEntity[] = await apiService.getRoutes();
        const route = routes.find(
          (r) =>
            Number(r.shopId) === Number(shop.id) &&
            Number(r.terminalId) === Number(terminal.id)
        );
        console.log("Route match (Mobility):", route);
        console.log(
          "Route message (Mobility):",
          route?.message?.text ?? route?.message ?? "нет сообщения"
        );

        if (!route || !route.points || route.points.length === 0) {
          setActiveRoutePoints(undefined);
          setActiveRouteMessage(undefined);
          setError(
            `Маршрут от стойки "${terminalName}" до объекта "${shopId}" не найден`
          );
          setLoading(false);
          hasBuiltRouteRef.current = routeKey || "error";
          isBuildingRef.current = false;
          return;
        }

        // Устанавливаем выбранный объект только если он действительно изменился
        const currentShopId = state.selectedShop?.id;
        if (currentShopId !== shop.id) {
          setSelectedShop(shop);
        }

        console.log("Route match (Mobility):", route);
        const routeMessage = extractRouteMessage(route?.message);
        console.log(
          "Route message (Mobility):",
          routeMessage ?? "нет сообщения"
        );
        setActiveRouteMessage(routeMessage);

        // Устанавливаем точки маршрута с небольшой задержкой,
        // чтобы убедиться, что selectedShop обновился
        setTimeout(() => {
          console.log("Route message:", routeMessage ?? "нет сообщения");
          setActiveRouteMessage(routeMessage);
          setActiveRoutePoints(route.points);
          setLoading(false);
          // Сохраняем ключ построенного маршрута
          hasBuiltRouteRef.current = routeKey || "error";
          isBuildingRef.current = false;
        }, 100);
      } catch (e: any) {
        console.error("Error building route:", e);
        setActiveRoutePoints(undefined);
        setActiveRouteMessage(undefined);
        setError(
          `Ошибка при построении маршрута: ${e.message || "Неизвестная ошибка"}`
        );
        setLoading(false);
        hasBuiltRouteRef.current = routeKey || "error";
        isBuildingRef.current = false;
      }
    };

    buildRoute();
    // Зависимости только от ключа маршрута - он стабилен благодаря useMemo
    // terminalName и shopId включены для полноты, но routeKey уже зависит от них
  }, [routeKey]);

  // Показываем страницу ошибки, если backend недоступен
  if (state.isBackendUnavailable) {
    return <ErrorPage />;
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Загрузка маршрута...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <MallMap />
    </div>
  );
}
